import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, Plus, Send, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useShop } from "../../contexts/ShopContext";
import ChatPage from "../../pages/ChatPage";
import ReportViewerContent from "../../pages/ReportsPage/ReportViewerContent";
import {
  collectionByKey,
} from "../../pages/ReportsPage/reportViewUtils";
import {
  ASSESSMENT_SUMMARY_ITEM_BY_ID,
  SUMMARY_SECTION_LABELS,
} from "../../utils/assessmentSummaryConfig";
import { fetchFoundationalBusinessRealitySummary } from "../../utils/api/api";
import { fetchMetaOverallReport } from "../../utils/api/reportApi";
import {
  attachChannelChatContext,
  removeChannelChatContext,
} from "../../utils/api/channelChatApi";
import {
  sanitizeChatContext,
  setChatContextPickMode,
  readDraggedChatContext,
} from "../../utils/chatContextDrag";
import {
  FoundationNarrativeAssessmentLineRenderer,
  isNarrativeAssessmentLine,
} from "../assessmentViewer/FoundationStage";
import { POST_AUTH_WELCOME } from "../../constants/postAuthWelcome";
import styles from "./ChannelWorkspace.module.css";

const PENDING_WELCOME_INTENT_KEY = "zeqi:pendingWelcomeIntent";
const ACTIVE_WELCOME_INTENT_KEY_PREFIX = "zeqi:activeWelcomeIntent:";
const INTEGRATION_GATE_COPY = {
  main: {
    title: "Let’s get Zeqi connected first",
    body:
      "Connect your store and marketing channels in Integrations so I can start building real assessments, reports, and recommendations instead of guessing from an empty workspace.",
    buttonLabel: "Open integrations",
  },
  default: {
    title: "This channel needs integrations first",
    body:
      "Connect your store and relevant marketing accounts in Integrations so I can analyze real business data and make this channel useful.",
    buttonLabel: "Open integrations",
  },
  "paid-ads": {
    title: "Connect your ad channels first",
    body:
      "I need your commerce data and advertising accounts connected before I can reason about paid performance, ROAS, creative tests, or channel efficiency.",
    buttonLabel: "Open integrations",
  },
  "meta-ads": {
    title: "Connect Meta and your store first",
    body:
      "Connect Shopify and Meta in Integrations so I can link ad spend, traffic, and real orders before we work inside this channel.",
    buttonLabel: "Open integrations",
  },
  "google-ads": {
    title: "Connect Google Ads and your store first",
    body:
      "Connect Shopify and Google in Integrations so I can connect clicks, spend, and revenue before we dig into Google Ads.",
    buttonLabel: "Open integrations",
  },
  "tiktok-ads": {
    title: "Connect TikTok and your store first",
    body:
      "Connect Shopify and TikTok in Integrations so I can evaluate campaign performance against real commerce outcomes.",
    buttonLabel: "Open integrations",
  },
  social: {
    title: "Connect your channels first",
    body:
      "Connect your store and social platforms in Integrations so I can ground content, audience, and competitive guidance in real business context.",
    buttonLabel: "Open integrations",
  },
  "content-cal": {
    title: "Connect your business data first",
    body:
      "Connect your store and channels in Integrations so I can build a content calendar around actual products, audiences, and demand patterns.",
    buttonLabel: "Open integrations",
  },
  seo: {
    title: "Connect Google and your store first",
    body:
      "Connect Shopify and Google in Integrations so I can work with actual search visibility, indexing, and product data in this SEO channel.",
    buttonLabel: "Open integrations",
  },
  google: {
    title: "Connect Google and your store first",
    body:
      "Connect Shopify and Google in Integrations so I can make the Google workspace useful with real Search Console and Merchant Center context.",
    buttonLabel: "Open integrations",
  },
  "google-search-console": {
    title: "Connect Google Search Console first",
    body:
      "Connect Shopify and Google in Integrations so I can inspect search visibility, indexing, and page performance with real data.",
    buttonLabel: "Open integrations",
  },
  "google-merchant-center": {
    title: "Connect Merchant Center first",
    body:
      "Connect Shopify and Google in Integrations so I can review feed health, product issues, and merchant readiness with real data.",
    buttonLabel: "Open integrations",
  },
  pdp: {
    title: "Connect your store first",
    body:
      "Connect Shopify in Integrations so I can inspect product pages, conversion patterns, and funnel behavior using real commerce data.",
    buttonLabel: "Open integrations",
  },
  email: {
    title: "Connect your channels first",
    body:
      "Connect Shopify and your email tools in Integrations so I can reason about retention, repeat purchase behavior, and lifecycle opportunities with real data.",
    buttonLabel: "Open integrations",
  },
};

const CHANNEL_HIGHLIGHTS = {
  main: {
    assessments: ["business"],
    reports: [
      { key: "benchmark", label: "Store summary" },
      { key: "meta", label: "Meta ads" },
      { key: "revenue_reality", label: "Revenue reality" },
    ],
  },
  // email: {
  //   assessments: ["retention", "content"],
  //   reports: [{ key: "benchmark", label: "Store summary" }],
  // },
  "paid-ads": {
    assessments: ["performance", "revenue", "conversion"],
    reports: [
      { key: "meta", label: "Meta ads" },
      { key: "revenue_reality", label: "Revenue reality" },
      { key: "target_audience", label: "Target audience" },
    ],
  },
  "meta-ads": {
    assessments: ["performance", "revenue", "conversion"],
    reports: [{ key: "meta", label: "Meta ads" }],
  },
  "google-ads": {
    assessments: ["performance", "conversion"],
    reports: [{ key: "target_audience", label: "Target audience" }],
  },
  "tiktok-ads": {
    assessments: ["performance", "conversion"],
    reports: [{ key: "target_audience", label: "Target audience" }],
  },
  social: {
    assessments: ["content", "visibility"],
    reports: [
      { key: "competitor", label: "Competitor analysis" },
      { key: "target_audience", label: "Target audience" },
    ],
  },
  "content-cal": {
    assessments: ["content"],
    reports: [{ key: "yearlyContent", label: "Monthly events calendar" }],
  },
  seo: {
    assessments: ["visibility"],
    reports: [
      { key: "google_search_console", label: "GSC" },
      { key: "market", label: "Market analysis" },
    ],
  },
  pdp: {
    assessments: ["conversion"],
    reports: [{ key: "conversion", label: "Conversion & PDP report" }],
  },
  google: {
    assessments: ["conversion", "visibility"],
    reports: [
      { key: "google_search_console", label: "Google search console" },
      { key: "google_merchant_center", label: "Google merchant center" },
    ],
  },
  "google-search-console": {
    assessments: ["visibility"],
    reports: [{ key: "google_search_console", label: "Google search console" }],
  },
  "google-merchant-center": {
    assessments: ["conversion", "visibility"],
    reports: [{ key: "google_merchant_center", label: "Google merchant center" }],
  },
};

const CHANNEL_COPY = {
  main: {
    prompt: "Give me a full  ",
    faq: [
      "Give me a full business assessment",
      "What are my top selling products?",
      "How's revenue trending this month?",
    ],
    actions: [
      { type: "assessment", label: "View business assessment" },
      { type: "report", key: "revenue_reality", label: "Revenue reality" },
      { type: "report", key: "benchmark", label: "Store summary" },
    ],
  },
  email: {
    prompt: "How's my email channel performing?",
    faq: [
      "What's my email list size?",
      "How can I improve repeat purchases?",
      "Create a post-purchase email sequence",
    ],
    actions: [
      { type: "assessment", label: "View retention assessment" },
      { type: "channel", channelId: "content-cal", label: "Content & creatives" },
    ],
  },
  "paid-ads": {
    prompt: "How are my paid ads performing?",
    faq: [
      "Which campaigns have the best ROAS?",
      "Should I increase my Meta ad spend?",
      "What audiences are performing best?",
    ],
    actions: [
      { type: "report", key: "meta", label: "Full Meta ads" },
      { type: "assessment", label: "Performance assessment" },
      { type: "report", key: "target_audience", label: "Target audience" },
    ],
  },
  "meta-ads": {
    prompt: "How are my Meta ads performing?",
    faq: [
      "Which Meta campaigns have the best ROAS?",
      "Should I increase Meta ad spend?",
      "What audiences are performing best on Meta?",
    ],
    actions: [
      { type: "report", key: "meta", label: "Full Meta ads" },
      { type: "assessment", label: "Performance assessment" },
    ],
  },
  "google-ads": {
    prompt: "How are my Google Ads campaigns performing?",
    faq: [
      "How is Google Ads traffic converting?",
      "What should I improve in my Google Ads setup?",
      "Which audiences should I test in Google Ads?",
    ],
    actions: [
      { type: "assessment", label: "Performance assessment" },
      { type: "report", key: "target_audience", label: "Target audience" },
    ],
  },
  "tiktok-ads": {
    prompt: "How are my TikTok ads performing?",
    faq: [
      "What should I test on TikTok ads next?",
      "How can I improve TikTok ad conversion quality?",
      "What audience should I target on TikTok?",
    ],
    actions: [
      { type: "assessment", label: "Performance assessment" },
      { type: "report", key: "target_audience", label: "Target audience" },
    ],
  },
  social: {
    prompt: "How's my social media presence?",
    faq: [
      "What should I post this week?",
      "How are competitors using social?",
      "Create a content plan for Instagram",
    ],
    actions: [
      { type: "assessment", label: "Content assessment" },
      { type: "report", key: "competitor", label: "Competitor analysis" },
      { type: "report", key: "target_audience", label: "Target audience" },
    ],
  },
  "content-cal": {
    prompt: "What content should I plan for the upcoming period?",
    faq: [
      "What events are coming up this month?",
      "Plan content for Eid Al-Fitr",
      "What seasonal campaigns should I run?",
    ],
    actions: [
      { type: "report", key: "yearlyContent", label: "Full events calendar" },
      { type: "channel", channelId: "social", label: "Content assessment" },
    ],
  },
  seo: {
    prompt: "How's my SEO and search visibility?",
    faq: [
      "How's my SEO indexing health?",
      "Which pages aren't indexed?",
      "How can I improve my search CTR?",
    ],
    actions: [
      { type: "report", key: "google_search_console", label: "GSC report" },
      { type: "assessment", label: "Visibility assessment" },
    ],
  },
  pdp: {
    prompt: "How are my PDPs and conversion funnel performing?",
    faq: [
      "Why is this PDP not converting?",
      "Which product page has the weakest add to cart rate?",
      "What should I fix first on this PDP?",
      "What patterns do you see across weak PDPs?",
    ],
    actions: [
      { type: "report", key: "conversion", label: "Conversion & PDP report" },
      { type: "assessment", label: "Conversion assessment" },
    ],
  },
  google: {
    prompt: "How are my Google surfaces performing?",
    faq: [
      "How is my Google search visibility doing?",
      "What is my Merchant Center trust status?",
      "Where are my Google opportunities right now?",
    ],
    actions: [
      {
        type: "channel",
        channelId: "google",
        subchannelId: "google-search-console",
        label: "Open Google Search Console",
      },
      {
        type: "channel",
        channelId: "google",
        subchannelId: "google-merchant-center",
        label: "Open Google Merchant Center",
      },
    ],
  },
  "google-search-console": {
    prompt: "Show me my Google Search Console data",
    faq: [
      "Show me my indexing coverage breakdown",
      "Which page types need attention?",
      "How's my core web vitals score?",
    ],
    actions: [
      { type: "report", key: "google_search_console", label: "Full GSC report" },
      { type: "assessment", label: "Visibility assessment" },
    ],
  },
  "google-merchant-center": {
    prompt: "What's my Google Merchant Center status?",
    faq: [
      "What's my Google Merchant Center trust score?",
      "Are there any product blockers?",
      "How can I improve my store quality?",
    ],
    actions: [
      { type: "report", key: "google_merchant_center", label: "Full GMC report" },
      { type: "assessment", label: "Visibility assessment" },
    ],
  },
};

const CHANNEL_LABELS = {
  main: "Main",
  email: "Email",
  integrations: "Integrations",
  "paid-ads": "Paid ads",
  "meta-ads": "Meta Ads",
  "google-ads": "Google Ads",
  "tiktok-ads": "TikTok Ads",
  social: "Social Media",
  "content-cal": "Content calendar",
  seo: "SEO / AEO",
  pdp: "CRO",
  google: "Google",
  "google-search-console": "Google search console",
  "google-merchant-center": "Google merchant center",
};

const buildPdpChatContext = () => {
  return {
    label: "CRO",
    type: "channel",
    key: "pdp",
    collectionName: "conversion_and_pdp_report",
    reportTitle: "Conversion & PDP",
  };
};

const buildPdpReportChatContext = (report) => {
  const reportId = report?._id || report?.id || null;
  return {
    label: "CRO",
    type: "report",
    key: reportId ? `report:${reportId}` : "pdp",
    reportId,
    collectionName: "conversion_and_pdp_report",
    reportTitle: "Conversion & PDP",
  };
};

const buildReportChatContext = (report, fallbackLabel = "Report") => {
  const reportId = report?._id || report?.id || null;
  const collectionName = report?.data?.collection_name || null;
  if (collectionName === "conversion_and_pdp_report") {
    return buildPdpReportChatContext(report);
  }
  return {
    label: fallbackLabel,
    type: "report",
    key: reportId ? `report:${reportId}` : `report:${collectionName || "current"}`,
    reportId,
    collectionName,
  };
};

const getSummarySectionLines = (section = null) => {
  if (!section || typeof section !== "object") return [];

  const lines = [];
  const summary = String(section.summary || "").trim();
  const assessment = String(section.assessment || "").trim();
  const description = String(section.description || "").trim();
  const overallDirection = String(
    section?.creative_guidelines?.overall_direction || "",
  ).trim();

  if (summary) lines.push(summary);
  if (assessment && assessment !== summary) lines.push(assessment);
  if (description && description !== summary && description !== assessment) {
    lines.push(description);
  }
  if (
    overallDirection &&
    !lines.some((line) => line.toLowerCase() === overallDirection.toLowerCase())
  ) {
    lines.push(overallDirection);
  }

  if (Array.isArray(section.assessment_report)) {
    section.assessment_report
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .forEach((item) => lines.push(item));
  }

  return lines;
};

const buildDefaultChatContext = (activeSubchannelId = "") =>
  activeSubchannelId && CHANNEL_LABELS[activeSubchannelId]
    ? {
        label: CHANNEL_LABELS[activeSubchannelId],
        type: "subchannel",
        key: activeSubchannelId,
      }
    : {
        label: "Main",
        type: "main",
        key: "main",
      };

const buildBaseChatContextTrail = (activeChannelId = "main", activeSubchannelId = "") => {
  const trail = [];

  if (activeChannelId && activeChannelId !== "main" && CHANNEL_LABELS[activeChannelId]) {
    trail.push({
      label: CHANNEL_LABELS[activeChannelId],
      type: "channel",
      key: activeChannelId,
    });
  }

  if (activeSubchannelId && CHANNEL_LABELS[activeSubchannelId]) {
    trail.push({
      label: CHANNEL_LABELS[activeSubchannelId],
      type: "subchannel",
      key: activeSubchannelId,
    });
  }

  if (!trail.length) {
    trail.push(buildDefaultChatContext(""));
  }

  return trail;
};

const DRAG_START_MOUSE_KEY = "__zeqiChannelWorkspaceStartMouseDrag";
const DRAG_START_TOUCH_KEY = "__zeqiChannelWorkspaceStartTouchDrag";
const ChannelWorkspace = ({
  activeChannelId = "main",
  activeSubchannelId = "",
  isContextOpen = false,
  contextSelection = null,
  onCloseContext = null,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const { t } = useLocalization();
  const { shopData } = useShop();
  const splitRef = useRef(null);
  const userId = user?._id || user?.id || user?.userId || "";
  const authToken = user?.token;
  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shopData?._id)
    ? shopData._id
    : isMongoId(shopData?.id)
      ? shopData.id
      : null;
  const [topFlex, setTopFlex] = useState(0.42);
  const [chatPaneState, setChatPaneState] = useState("default");
  const [dismissedContextKeys, setDismissedContextKeys] = useState([]);
  const [extraChatContexts, setExtraChatContexts] = useState([]);
  const chatFocusContextLoadedRef = useRef(false);
  const chatSessionIdRef = useRef(null);
  const [welcomeIntent, setWelcomeIntent] = useState(null);
  const [summaryReportData, setSummaryReportData] = useState(null);
  const [headerSlotEl, setHeaderSlotEl] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 900px)").matches,
  );
  const [headerPickModeActive, setHeaderPickModeActive] = useState(false);
  const [isHeaderDropActive, setIsHeaderDropActive] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [metaOverallReport, setMetaOverallReport] = useState(null);
  const isChatClosed = isContextOpen && chatPaneState === "minimizedChat";
  const hasShopifyConnection = Boolean(
    user?.shopifyToken ||
      user?.shop ||
      shopData?._id ||
      shopData?.id ||
      shopData?.myshopifyDomain ||
      (Array.isArray(user?.authProviders) &&
        user.authProviders.some((item) => item?.provider === "shopify")),
  );
  const hasGoogleConnection = Boolean(
    user?.googleToken ||
      user?.googleRefreshToken ||
      user?.onboarding?.integrations?.googleConnected,
  );
  const hasMetaConnection = Boolean(
    user?.facebookToken ||
      user?.onboarding?.integrations?.metaConnected,
  );
  const hasAnyIntegrationConnection =
    hasShopifyConnection || hasGoogleConnection || hasMetaConnection;

  const reportByCollection = useMemo(() => {
    const idx = {};
    if (metaOverallReport) {
      idx["shop_meta"] = metaOverallReport;
    }
    return idx;
  }, [metaOverallReport]);

  const effectiveContextId = activeSubchannelId || activeChannelId;
  const copy = CHANNEL_COPY[effectiveContextId] || CHANNEL_COPY.main;
  const highlights =
    CHANNEL_HIGHLIGHTS[effectiveContextId] || CHANNEL_HIGHLIGHTS.main;
  const baseChatContextTrail = useMemo(
    () => buildBaseChatContextTrail(activeChannelId, activeSubchannelId),
    [activeChannelId, activeSubchannelId],
  );
  const visibleBaseChatContextTrail = useMemo(() => {
    const next = baseChatContextTrail.filter(
      (context) => !dismissedContextKeys.includes(context.key),
    );
    return next.length ? next : [buildDefaultChatContext("")];
  }, [baseChatContextTrail, dismissedContextKeys]);
  const chatContextTrail = useMemo(() => {
    const next = [...visibleBaseChatContextTrail];
    for (const ctx of extraChatContexts) {
      if (
        ctx?.key &&
        !dismissedContextKeys.includes(ctx.key) &&
        !next.some((c) => c.key === ctx.key)
      ) {
        next.push(ctx);
      }
    }
    console.log("[ChannelWorkspace:chatContextTrail] computed", { trail: next.map((c) => ({ key: c.key, label: c.label, type: c.type })), extraChatContexts: extraChatContexts.map((c) => ({ key: c.key, type: c.type })), dismissed: dismissedContextKeys });
    return next;
  }, [visibleBaseChatContextTrail, extraChatContexts, dismissedContextKeys]);
  const activeChatContext = useMemo(() => {
    let items = chatContextTrail.length
      ? chatContextTrail
      : [buildDefaultChatContext("")];
    const hasReportContext = items.some((item) => item?.reportId);
    if (effectiveContextId === "pdp" && !hasReportContext) {
      items = [...items, buildPdpChatContext()];
    }
    return sanitizeChatContext(items) || buildDefaultChatContext("");
  }, [chatContextTrail, effectiveContextId]);
  const chatSuggestions = useMemo(
    () =>
      copy.faq.map((question, index) => ({
        id: `${effectiveContextId}-${index}`,
        text: question,
        theme: "info",
      })),
    [copy.faq, effectiveContextId],
  );

  useEffect(() => {
    if (typeof window === "undefined" || !userId) return;
    try {
      const pendingIntent = window.sessionStorage.getItem(PENDING_WELCOME_INTENT_KEY);
      const activeIntentKey = `${ACTIVE_WELCOME_INTENT_KEY_PREFIX}${userId}`;
      const activeIntent = window.sessionStorage.getItem(activeIntentKey);
      const nextIntent = pendingIntent || activeIntent || null;
      if (!nextIntent) return;
      setWelcomeIntent(nextIntent);
      window.sessionStorage.setItem(activeIntentKey, nextIntent);
      if (pendingIntent) {
        window.sessionStorage.removeItem(PENDING_WELCOME_INTENT_KEY);
      }
    } catch {
      // ignore session storage issues
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !welcomeIntent || typeof window === "undefined") return;

    const isResolved =
      welcomeIntent === "shopify_post_auth"
        ? hasGoogleConnection && hasMetaConnection
        : hasShopifyConnection;

    if (!isResolved) return;

    try {
      window.sessionStorage.removeItem(`${ACTIVE_WELCOME_INTENT_KEY_PREFIX}${userId}`);
      window.sessionStorage.removeItem(PENDING_WELCOME_INTENT_KEY);
    } catch {
      // ignore session storage issues
    }
    setWelcomeIntent(null);
  }, [
    hasGoogleConnection,
    hasMetaConnection,
    hasShopifyConnection,
    userId,
    welcomeIntent,
  ]);

  const welcomeConfig = welcomeIntent ? POST_AUTH_WELCOME[welcomeIntent] || null : null;
  const shouldShowWelcomeMessage =
    activeChannelId === "main" &&
    !activeSubchannelId &&
    Boolean(welcomeConfig) &&
    (welcomeIntent === "shopify_post_auth"
      ? !(hasGoogleConnection && hasMetaConnection)
      : !hasShopifyConnection);
  const integrationGateConfig = useMemo(() => {
    if (hasAnyIntegrationConnection || activeChannelId === "integrations") return null;
    return (
      INTEGRATION_GATE_COPY[effectiveContextId] ||
      INTEGRATION_GATE_COPY[activeChannelId] ||
      INTEGRATION_GATE_COPY.default
    );
  }, [activeChannelId, effectiveContextId, hasAnyIntegrationConnection]);
  const leadingAiMessage = useMemo(() => {
    if (integrationGateConfig) {
      return {
        type: "ai",
        localId: `integration-gate:${effectiveContextId}`,
        data: {
          id: `integration-gate:${effectiveContextId}`,
          content: `**${integrationGateConfig.title}**\n\n${integrationGateConfig.body}`,
          createdAt: new Date().toISOString(),
          cta: {
            label: integrationGateConfig.buttonLabel,
            onClick: () => navigate("/integrations"),
          },
        },
      };
    }
    if (!shouldShowWelcomeMessage || !welcomeConfig) return null;
    return {
      type: "ai",
      localId: `welcome:${welcomeIntent}`,
      data: {
        id: `welcome:${welcomeIntent}`,
        content: `**${welcomeConfig.title}**\n\n${welcomeConfig.body}`,
        createdAt: new Date().toISOString(),
        cta: {
          label: welcomeConfig.buttonLabel,
          onClick: () => navigate("/integrations"),
        },
      },
    };
  }, [
    effectiveContextId,
    integrationGateConfig,
    navigate,
    shouldShowWelcomeMessage,
    welcomeConfig,
    welcomeIntent,
  ]);
  const localGuardReply = useMemo(() => {
    if (!integrationGateConfig) return null;
    return {
      content: `I’m blocked until your integrations are connected.\n\n${integrationGateConfig.body}`,
      cta: {
        label: integrationGateConfig.buttonLabel,
        onClick: () => navigate("/integrations"),
      },
    };
  }, [integrationGateConfig, navigate]);

  // Fetch meta overall data for right-rail report view (no campaign_performance_raw)
  useEffect(() => {
    let active = true;
    const needsMeta = (highlights.reports || []).some((r) => r.key === "meta");
    if (!needsMeta || !userId || !authToken) return undefined;

    fetchMetaOverallReport({ userId, shopId, authToken })
      .then((response) => {
        if (!active) return;
        setMetaOverallReport(response?.report || null);
      })
      .catch(() => {
        if (!active) return;
        setMetaOverallReport(null);
      });

    return () => { active = false; };
  }, [authToken, shopId, userId, highlights.reports]);

  useEffect(() => {
    let active = true;
    if (!userId || !authToken) return undefined;

    setSummaryLoading(true);
    setSummaryError("");

    fetchFoundationalBusinessRealitySummary({
      userId,
      shopId,
      authToken,
    })
      .then((response) => {
        if (!active) return;
        setSummaryReportData(response || null);
      })
      .catch((error) => {
        if (!active) return;
        setSummaryError(error?.message || "Unable to load channel assessment.");
        setSummaryReportData(null);
      })
      .finally(() => {
        if (!active) return;
        setSummaryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authToken, shopId, userId]);

  const summarySections = summaryReportData?.sections || null;
  const businessAssessmentReportBody = String(
    summaryReportData?.report_body || "",
  ).trim();
  const combinedFlow = useMemo(() => {
    return (highlights.assessments || [])
      .map((assessmentId) => {
        const summaryMeta = ASSESSMENT_SUMMARY_ITEM_BY_ID[assessmentId];
        if (!summaryMeta) return null;

        const assessmentSections = (summaryMeta.sectionKeys || [])
          .map((sectionKey) => {
            const payload = summarySections?.[sectionKey];
            const lines = getSummarySectionLines(payload);
            if (!payload || !lines.length) return null;
            const assessmentReport = Array.isArray(payload?.assessment_report)
              ? payload.assessment_report
                  .map((item) => String(item || "").trim())
                  .filter(Boolean)
              : [];
            return {
              key: sectionKey,
              title: SUMMARY_SECTION_LABELS[sectionKey] || sectionKey,
              body: lines.join("\n\n"),
              assessmentReportItems: assessmentReport,
            };
          })
          .filter(Boolean);

        if (!assessmentSections.length) return null;

        return {
          id: assessmentId,
          label: summaryMeta.label,
          assessmentSections,
        };
      })
      .filter(Boolean);
  }, [highlights.assessments, summarySections]);

  const selectedContextSelection = useMemo(() => {
    if (contextSelection?.type) return contextSelection;
    return { type: "assessment" };
  }, [contextSelection]);

  const shouldRenderBusinessAssessmentBody =
    selectedContextSelection?.type !== "report" &&
    effectiveContextId === "main" &&
    (highlights.assessments || []).includes("business") &&
    Boolean(businessAssessmentReportBody);

  const selectedInlineReport = useMemo(() => {
    if (selectedContextSelection?.type !== "report") return null;
    const collection = collectionByKey[selectedContextSelection.key];
    return collection ? reportByCollection[collection] || null : null;
  }, [reportByCollection, selectedContextSelection]);

  const updateSplitRatio = useCallback((clientY) => {
    const container = splitRef.current;
    if (!(container instanceof HTMLElement)) return;
    const rect = container.getBoundingClientRect();
    const pct = (clientY - rect.top) / rect.height;
    if (pct <= 0.1 || pct >= 0.9) return;
    setChatPaneState("default");
    setTopFlex(pct);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event) => updateSplitRatio(event.clientY);
    const handleTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      updateSplitRatio(touch.clientY);
    };
    const handleEnd = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleEnd);
    };

    window[DRAG_START_MOUSE_KEY] = () => {
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleEnd);
    };

    window[DRAG_START_TOUCH_KEY] = () => {
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleEnd);
    };

    return () => {
      delete window[DRAG_START_MOUSE_KEY];
      delete window[DRAG_START_TOUCH_KEY];
      handleEnd();
    };
  }, [updateSplitRatio]);

  useEffect(() => {
    chatFocusContextLoadedRef.current = false;
    chatSessionIdRef.current = null;
    setChatPaneState("default");
    setTopFlex(0.42);
    setDismissedContextKeys([]);
    setExtraChatContexts([]);
  }, [userId, activeChannelId, activeSubchannelId]);

  useEffect(() => {
    const el = document.getElementById("header-tabs-slot");
    setHeaderSlotEl(el || null);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    setChatContextPickMode(headerPickModeActive);
    return () => { if (headerPickModeActive) setChatContextPickMode(false); };
  }, [headerPickModeActive]);

  useEffect(() => {
    if (!headerPickModeActive) return undefined;
    const handleEscape = (e) => {
      if (e.key === "Escape") setHeaderPickModeActive(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [headerPickModeActive]);

  const handleChatSessionIdReady = useCallback((sid) => {
    if (sid) {
      chatSessionIdRef.current = sid;
      console.log("[ChannelWorkspace] chatSessionIdRef set", { sessionId: sid, channelId: activeChannelId });
    }
  }, [activeChannelId]);

  const handleSessionContextLoaded = useCallback((trail, detachedKeys) => {
    if (Array.isArray(detachedKeys) && detachedKeys.length) {
      setDismissedContextKeys(detachedKeys);
    }
    if (Array.isArray(trail) && trail.length) {
      const baseKeys = new Set(baseChatContextTrail.map((c) => c.key));
      const extraItems = trail.filter((item) => item?.key && !baseKeys.has(item.key));
      setExtraChatContexts(extraItems);
    }
    chatFocusContextLoadedRef.current = true;
  }, [baseChatContextTrail]);

  const addChatFocusContext = useCallback((nextContext, { silent = false } = {}) => {
    if (!nextContext?.key) return;
    console.log("[ChannelWorkspace:addChatFocusContext] called", { key: nextContext.key, type: nextContext.type, label: nextContext.label });
    setDismissedContextKeys((prev) => prev.filter((k) => k !== nextContext.key));
    setExtraChatContexts((prev) => {
      const replaceSingletonType =
        nextContext.type === "pdp-product" || nextContext.type === "report-window";
      const without = prev.filter((c) => {
        if (c?.key === nextContext.key) return false;
        if (replaceSingletonType && c?.type === nextContext.type) return false;
        return true;
      });
      return [...without, nextContext];
    });
    if (!silent && nextContext?.label) {
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-context-notification", {
          detail: { type: "added", label: nextContext.label, key: nextContext.key },
        }),
      );
    }
    const sessionId = chatSessionIdRef.current;
    console.log("[ChannelWorkspace] addChatFocusContext", {
      sessionId,
      userId,
      hasAuthToken: Boolean(authToken),
      contextKey: nextContext?.key,
      contextType: nextContext?.type,
    });
    if (sessionId && userId && authToken) {
      attachChannelChatContext({
        userId,
        sessionId,
        channelId: activeChannelId,
        context: nextContext,
        authToken,
      }).then((res) => {
        console.log("[ChannelWorkspace] attachChannelChatContext.ok", { sessionId, contextKey: nextContext?.key, trail: res?.contextTrail?.map((c) => c.key) });
      }).catch((err) => {
        console.warn("[ChannelWorkspace] attachChannelChatContext.failed", err?.message || err);
      });
    } else {
      console.warn("[ChannelWorkspace] attachChannelChatContext.skipped — missing", {
        noSessionId: !sessionId,
        noUserId: !userId,
        noAuthToken: !authToken,
      });
    }
  }, [userId, authToken, activeChannelId]);

  const handleOpenChannel = useCallback((channelId, nextContext = null) => {
    if (nextContext) {
      addChatFocusContext(nextContext);
    }
    window.dispatchEvent(
      new CustomEvent("zeqi:set-active-channel", {
        detail: { channelId, subchannelId: "" },
      }),
    );
  }, [addChatFocusContext]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleAutoChatContext = (event) => {
      const { channelId, context } = event?.detail || {};
      if (!context?.key) return;
      const matchesChannel =
        !channelId ||
        channelId === activeChannelId ||
        channelId === activeSubchannelId ||
        channelId === effectiveContextId ||
        (channelId === "pdp" && context.collectionName === "conversion_and_pdp_report");
      console.log("[ChannelWorkspace:autoContext] received zeqi:add-chat-context", { channelId, contextKey: context.key, contextType: context.type, matchesChannel, activeChannelId, effectiveContextId });
      if (!matchesChannel) return;
      addChatFocusContext(context, { silent: true });
    };
    window.addEventListener("zeqi:add-chat-context", handleAutoChatContext);
    return () => {
      window.removeEventListener("zeqi:add-chat-context", handleAutoChatContext);
    };
  }, [activeChannelId, activeSubchannelId, effectiveContextId, addChatFocusContext]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleRemoveChatContext = (event) => {
      const { key, type } = event?.detail || {};
      console.log("[ChannelWorkspace:removeContext] received zeqi:remove-chat-context", { key, type });
      setExtraChatContexts((prev) => {
        if (key) return prev.filter((c) => c?.key !== key);
        if (type) return prev.filter((c) => c?.type !== type);
        return prev;
      });
    };
    window.addEventListener("zeqi:remove-chat-context", handleRemoveChatContext);
    return () => {
      window.removeEventListener("zeqi:remove-chat-context", handleRemoveChatContext);
    };
  }, []);

  const dismissChatContext = useCallback((contextKey) => {
    if (!contextKey || contextKey === "main") return;
    const item = chatContextTrail.find((c) => c.key === contextKey);
    setDismissedContextKeys((prev) =>
      prev.includes(contextKey) ? prev : [...prev, contextKey],
    );
    setExtraChatContexts((prev) => prev.filter((c) => c?.key !== contextKey));
    if (item?.label) {
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-context-notification", {
          detail: { type: "removed", label: item.label, key: contextKey },
        }),
      );
    }
    const sessionId = chatSessionIdRef.current;
    if (sessionId && userId && authToken) {
      removeChannelChatContext({
        userId,
        sessionId,
        contextKey,
        authToken,
      }).catch((err) => {
        console.warn("[channel-chat] remove context failed", err?.message || err);
      });
    }
  }, [chatContextTrail, userId, authToken]);

  const handleExpandChat = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "minimizedChat") return "default";
      if (current === "default") return "maximizedChat";
      return "default";
    });
  }, []);

  const handleExpandReports = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "maximizedChat") return "default";
      if (current === "default") return "minimizedChat";
      if (current === "maximizedTop") return "default";
      return "minimizedChat";
    });
  }, []);

  const headerContextTrailExtra = chatContextTrail.filter(
    (c) => c.key !== "main" && !baseChatContextTrail.some((b) => b.key === c.key),
  );

  return (
    <div className={styles.workspace}>
      {isDesktop && headerSlotEl ? createPortal(
        <div
          className={`${styles.headerContextStrip} ${isHeaderDropActive ? styles.headerContextStripDropActive : ""}`}
          onDragOver={(e) => { e.preventDefault(); setIsHeaderDropActive(true); }}
          onDragEnter={(e) => { e.preventDefault(); setIsHeaderDropActive(true); }}
          onDragLeave={(e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            setIsHeaderDropActive(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsHeaderDropActive(false);
            const dropped = readDraggedChatContext(e);
            if (dropped?.key) addChatFocusContext(dropped);
            setHeaderPickModeActive(false);
          }}
        >
          {headerContextTrailExtra.map((context) => (
            <span key={context.key} className={styles.headerContextPill}>
              <span className={styles.headerContextPillText}>{context.label}</span>
              <button
                type="button"
                className={styles.headerContextPillReset}
                onClick={() => dismissChatContext(context.key)}
                aria-label={`Remove ${context.label} context`}
              >
                <X size={10} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </span>
          ))}
          <button
            type="button"
            className={`${styles.headerContextAddBtn} ${headerPickModeActive ? styles.headerContextAddBtnActive : ""}`}
            onClick={() => setHeaderPickModeActive((prev) => !prev)}
            aria-label={headerPickModeActive ? "Cancel context pick" : "Add context to chat"}
            title={headerPickModeActive ? "Click to cancel, or drag a report here" : "Add context to chat"}
          >
            {headerPickModeActive ? <X size={12} strokeWidth={2.2} /> : <Plus size={12} strokeWidth={2.2} />}
          </button>
        </div>,
        headerSlotEl,
      ) : null}
      <div ref={splitRef} className={styles.splitShell}>
        {isContextOpen ? (
          <div
            className={`${styles.contextPane} ${
              chatPaneState === "maximizedTop" ? styles.contextPaneMaximized : ""
            } ${chatPaneState === "minimizedChat" ? styles.contextPaneExpanded : ""}`}
            style={chatPaneState === "default" ? { flex: topFlex } : undefined}
          >
            <section
              className={`${styles.mixedFeed} ${
                selectedContextSelection?.type === "report"
                  ? styles.mixedFeedFill
                  : ""
              }`}
            >
              {selectedContextSelection?.type === "report" ? (
                selectedInlineReport ? (
                  <article className={`${styles.viewerCard} ${styles.viewerCardFill}`}>
                    <div className={styles.viewerHeader}>
                      <div>
                        <p className={styles.viewerEyebrow}>Report</p>
                        <h3 className={styles.viewerTitle}>
                          {selectedContextSelection.label || "Report"}
                        </h3>
                      </div>
                      <button
                        type="button"
                        className={styles.viewerDiscuss}
                        onClick={() => {
                          const nextContext = buildReportChatContext(
                            selectedInlineReport,
                            selectedContextSelection.label ||
                              selectedInlineReport?.data?.collection_name ||
                              "Report",
                          );
                          if (
                            selectedInlineReport?.data?.collection_name ===
                            "conversion_and_pdp_report"
                          ) {
                            handleOpenChannel("pdp", nextContext);
                            return;
                          }
                          addChatFocusContext(nextContext);
                        }}
                      >
                        {selectedInlineReport?.data?.collection_name ===
                        "conversion_and_pdp_report"
                          ? "Discuss in CRO"
                          : "Discuss"}
                      </button>
                    </div>
                    <div
                      className={`${styles.viewerBody} ${styles.viewerBodyScrollable}`}
                    >
                      <ReportViewerContent
                        report={selectedInlineReport}
                        onOpenChannel={handleOpenChannel}
                      />
                    </div>
                  </article>
                ) : (
                  <article className={styles.viewerCard}>
                    <div className={styles.viewerHeader}>
                      <div>
                        <p className={styles.viewerEyebrow}>Report</p>
                        <h3 className={styles.viewerTitle}>
                          {selectedContextSelection.label || "Report unavailable"}
                        </h3>
                      </div>
                    </div>
                    <div className={styles.emptyFeedState}>
                      No report is available for this channel yet.
                    </div>
                  </article>
                )
              ) : (
                <>
                  {shouldRenderBusinessAssessmentBody ? (
                    <article className={`${styles.viewerCard} ${styles.viewerCardFill}`}>
                      <div
                        className={`${styles.viewerBody} ${styles.viewerBodyScrollable}`}
                      >
                        <div className={styles.assessmentMarkdown}>
                          <ReactMarkdown>{businessAssessmentReportBody}</ReactMarkdown>
                        </div>
                      </div>
                    </article>
                  ) : (
                    combinedFlow.map((group) => (
                      <div key={group.id} className={styles.feedGroup}>
                        {group.assessmentSections.length ? (
                          <article className={styles.viewerCard}>
                            {/* <div className={styles.viewerHeader}>
                              <div>
                                <p className={styles.viewerEyebrow}>Assessment</p>
                                <h3 className={styles.viewerTitle}>{group.label}</h3>
                              </div>
                              <button
                                type="button"
                                className={styles.viewerDiscuss}
                                onClick={() =>
                                  setChatFocusContext({
                                    label: group.label,
                                    type: "assessment",
                                    key: group.id,
                                  })
                                }
                              >
                                Discuss
                              </button>
                            </div> */}
                            <div className={styles.assessmentFeed}>
                              {group.assessmentSections.map((section) => (
                                <section
                                  key={`${group.id}-${section.key}-assessment`}
                                  className={styles.assessmentBlock}
                                >
                                  <h4 className={styles.assessmentBlockTitle}>
                                    {section.title}
                                  </h4>
                                  {section.assessmentReportItems.length ? (
                                    <div className={styles.assessmentItems}>
                                      {section.assessmentReportItems.map((entry, index) => (
                                        <div
                                          key={`${section.key}-${index}`}
                                          className={styles.assessmentItem}
                                        >
                                          {isNarrativeAssessmentLine(entry) ? (
                                            <FoundationNarrativeAssessmentLineRenderer
                                              markdown={entry}
                                            />
                                          ) : (
                                            <div className={styles.assessmentMarkdown}>
                                              <ReactMarkdown>{entry}</ReactMarkdown>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className={styles.assessmentMarkdown}>
                                      <ReactMarkdown>{section.body}</ReactMarkdown>
                                    </div>
                                  )}
                                </section>
                              ))}
                            </div>
                          </article>
                        ) : null}
                      </div>
                    ))
                  )}

                  {!combinedFlow.length ? (
                    <article className={styles.viewerCard}>
                      <div className={styles.viewerHeader}>
                        <div>
                          <p className={styles.viewerEyebrow}>Channel view</p>
                          <h3 className={styles.viewerTitle}>No channel content yet</h3>
                        </div>
                      </div>
                      <div className={styles.emptyFeedState}>
                        {summaryLoading
                          ? t("common.loading") || "Loading"
                          : summaryError || "Current assessments will appear here once they are ready."}
                      </div>
                    </article>
                  ) : null}
                </>
              )}
            </section>
          </div>
        ) : null}

        {isContextOpen && !isChatClosed ? <div className={styles.paneDivider}>
          <button
            type="button"
            className={styles.dragHandle}
            aria-label="Resize panels"
            onMouseDown={(event) => {
              event.preventDefault();
              window[DRAG_START_MOUSE_KEY]?.();
            }}
            onTouchStart={(event) => {
              event.preventDefault();
              window[DRAG_START_TOUCH_KEY]?.();
            }}
          />
          <div className={styles.dividerControls}>
            <div className={styles.chatTitleWrap}>
              <span className={styles.chatTitle}>Zeqi chat</span>
              <div className={styles.chatContextTrail}>
                {chatContextTrail.map((context) => {
                  const isMainPill = context.key === "main";
                  return (
                    <span key={context.key} className={styles.chatContextPill}>
                      <span className={styles.chatContextPillText}>
                        {context.label || "Main"}
                      </span>
                      {!isMainPill ? (
                        <button
                          type="button"
                          className={styles.chatContextPillReset}
                          onClick={() => dismissChatContext(context.key)}
                          aria-label={`Remove ${context.label} context`}
                          title={`Remove ${context.label} context`}
                        >
                          <X size={12} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className={styles.controlButtons}>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleExpandChat}
                title="Expand chat"
              >
                <ArrowUp size={14} strokeWidth={2.2} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleExpandReports}
                title="Expand reports"
              >
                <ArrowDown size={14} strokeWidth={2.2} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={styles.controlButton}
                onClick={() => {
                  if (typeof onCloseContext === "function") {
                    onCloseContext();
                    return;
                  }
                  setChatPaneState("minimizedChat");
                }}
                title="Close panel"
              >
                <X size={14} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div> : null}

        <div
          className={`${styles.chatPane} ${
            chatPaneState === "maximizedChat" ? styles.chatPaneMaximized : ""
          } ${isChatClosed ? styles.chatPaneMinimized : ""} ${
            !isContextOpen ? styles.chatPaneFull : ""
          }`}
          style={
            isContextOpen && chatPaneState === "default"
              ? { flex: 1 - topFlex }
              : undefined
          }
        >
          <div className={styles.chatBody}>
            <ChatPage
              key={activeChannelId}
              inline
              mode="channel"
              channelId={activeChannelId}
              chatContext={activeChatContext}
              onContextResolved={(nextContext) => {
                if (
                  nextContext?.key &&
                  !dismissedContextKeys.includes(nextContext.key)
                ) {
                  addChatFocusContext(nextContext, { silent: true });
                }
              }}
              onSessionContextLoaded={handleSessionContextLoaded}
              onSessionIdReady={handleChatSessionIdReady}
              variant="inline"
              suggestionsOverride={chatSuggestions}
              inputPlaceholder={`Ask Zeqi about ${activeChatContext?.label || "Main"}...`}
              leadingAiMessage={leadingAiMessage}
              localGuardReply={localGuardReply}
            />
          </div>
        </div>
      </div>
      {isContextOpen && isChatClosed ? (
        <div className={styles.chatReopenDock}>
          <button
            type="button"
            className={styles.chatReopenField}
            onClick={() => setChatPaneState("default")}
            aria-label="Reopen Zeqi chat"
            title="Reopen Zeqi chat"
          >
            <span className={styles.chatReopenFieldIcon}>
              <Plus size={18} strokeWidth={1.9} aria-hidden="true" />
            </span>
            <span className={styles.chatReopenFieldText}>
              Ask Zeqi about {activeChatContext?.label || "Main"}...
            </span>
            <span className={styles.chatReopenFieldSend}>
              <Send size={16} strokeWidth={1.9} aria-hidden="true" />
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default ChannelWorkspace;
